import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '@config/AppConfigContext';
import { fetchJson, getAuthHeader, joinUrl } from '@hooks/requests/utils/_partner_api_client';
import { PARTNER_TEAM_PATH } from '@hooks/requests/utils/paths';

export interface RemoveMemberParams {
  memberId: string;
}

export const useRemoveMemberMutation = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId }: RemoveMemberParams): Promise<void> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');
      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);

      await fetchJson<void>(
        joinUrl(allyApi, `${PARTNER_TEAM_PATH}/${encodeURIComponent(memberId)}`),
        authHeader,
        'Failed to remove the member',
        { method: 'DELETE' }
      );
    },
    // FR-021: a 404 here means someone else already removed this member — the
    // roster still needs refetching to drop the stale row, not just on 2xx.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_TEAM_PATH] });
    },
  });
};

export default useRemoveMemberMutation;
